import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge, Flex, Table, UnstyledButton } from '@mantine/core'
import dayjs from 'dayjs'

import type { TableItemProps } from '@/modules/actionPlan/pages/components/TableItem/TableItem.types'

import { MoreDropdown } from '@/components/MoreDropdown/MoreDropdown'
import { Progress } from '@/components/Progress/Progress'
import { Text } from '@/components/Text/Text'

import { formatMoney } from '@/helpers/formatMoney.helper'

import classes from './TableItem.module.css'

const STATUS_BADGE_MAP: Record<string, { color: string; label: string }> = {
  ACTIVE: { color: 'green', label: 'Active' },
  ARCHIVED: { color: 'gray', label: 'Archived' },
  COMPLETED: { color: 'blue', label: 'Completed' },
}

export function TableItem({
  empty = false,
  item,
  openArchiveModal,
  openClaimModal,
  openCompleteModal,
  openDeleteModal,
  openEditModal,
  openReactivateModal,
  readOnly,
}: TableItemProps) {
  const navigate = useNavigate()

  const percentageValue = useMemo(() => {
    if (!item?.amount) return 0

    return Math.floor(((item.collectedAmount || 0) / item.amount) * 100)
  }, [item])

  if (!item || empty) {
    return (
      <Table.Tr className={classes.emptyTableItem}>
        <Table.Td miw="100%">
          <Text variant="body_2_semibold_black">No goals</Text>
        </Table.Td>
      </Table.Tr>
    )
  }

  const dropdownItems: Array<{
    label: string
    onClick: (id: string | undefined) => void
    param: string
  }> = []

  if (openEditModal) {
    dropdownItems.push({
      label: 'Edit',
      onClick: openEditModal,
      param: item.id.toString(),
    })
  }

  if (openClaimModal) {
    dropdownItems.push({
      label: 'Log',
      onClick: openClaimModal,
      param: item.id.toString(),
    })
  }

  if (openCompleteModal) {
    dropdownItems.push({
      label: 'Complete',
      onClick: openCompleteModal,
      param: item.id.toString(),
    })
  }

  if (openArchiveModal) {
    dropdownItems.push({
      label: 'Archive',
      onClick: openArchiveModal,
      param: item.id.toString(),
    })
  }

  if (openReactivateModal) {
    dropdownItems.push({
      label: 'Reactivate',
      onClick: openReactivateModal,
      param: item.id.toString(),
    })
  }

  if (openDeleteModal) {
    dropdownItems.push({
      label: 'Delete',
      onClick: openDeleteModal,
      param: item.id.toString(),
    })
  }

  const badge = STATUS_BADGE_MAP[item.status]

  return (
    <Table.Tr className={classes.tableItem}>
      <Table.Td miw={220} w="25%">
        <Flex align="center" gap={8}>
          {readOnly ? (
            <Text variant="body_2_semibold_black">{item.name}</Text>
          ) : (
            <UnstyledButton onClick={() => navigate(`/action-plan/${item.id}`)}>
              <Text variant="body_2_semibold_black">{item.name}</Text>
            </UnstyledButton>
          )}
          {badge && (
            <Badge color={badge.color} size="sm" variant="light">
              {badge.label}
            </Badge>
          )}
        </Flex>
      </Table.Td>
      <Table.Td miw={140} w="15%">
        <Text variant="body_2_medium_black">
          {dayjs(item.startDate).format('MMM D, YYYY')}
        </Text>
      </Table.Td>
      <Table.Td miw={140} w="15%">
        <Text variant="body_2_medium_black">
          {dayjs(item.dueDate).format('MMM D, YYYY')}
        </Text>
      </Table.Td>
      <Table.Td miw={140} w="15%">
        <Flex>
          <Text variant="body_2_semibold_grey">
            {formatMoney(item.collectedAmount)}/
          </Text>
          <Text variant="body_2_semibold_black">
            {formatMoney(item.amount)}
          </Text>
        </Flex>
      </Table.Td>
      <Table.Td miw={250}>
        <Flex align="center" gap={16}>
          <Progress value={percentageValue} w="100%" />
          <Text variant="body_2_semibold_black">{percentageValue}%</Text>
        </Flex>
      </Table.Td>
      <Table.Td w={24}>
        {dropdownItems.length > 0 && <MoreDropdown items={dropdownItems} />}
      </Table.Td>
    </Table.Tr>
  )
}
